import { CheckPointType } from '@/api/entities/check-point';
import Card from '@/components/Card';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import { useGetChildTab } from '@/hooks/tab.hooks';
import { useUploadGroupCustomImage, useUpdateGroup } from '@/hooks/group.hooks';
import { FormManagementProps } from '@/type/form.types';
import { GroupCheckPointTab } from '@/type/ui/tab/groupTab';
import { useEffect } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { GroupCheckPointLicensePlate } from '../GroupCheckPointLicensePlate';
import { GroupCheckPointBackgrounImage } from '../GroupCheckPointBackgrounImage';
import { GroupCheckPointWatermark } from '../GroupCheckPointWatermark';

interface GroupCheckPointManagementProps<
  TSchema extends FieldValues,
> extends FormManagementProps<TSchema> {
  checkPointType: CheckPointType;
  cardTitle: React.ReactNode;
  blurLicensePlateField: string;
  licensePlateImageField: string;
  backgroundImageField: string;
  watermarkField: string;
  watermarkPositionField: string;
  mutate?: (data: TSchema) => void;
}

export const GroupCheckPointManagement = <TSchema extends FieldValues>({
  t,
  checkPointType,
  cardTitle,
  blurLicensePlateField,
  licensePlateImageField,
  backgroundImageField,
  watermarkField,
  watermarkPositionField,
  data,
  register,
  setValue,
  watch,
  submitDisabled,
  mutate,
}: GroupCheckPointManagementProps<TSchema>) => {
  const { currentTab, navigateToTab } = useGetChildTab({
    // defaultTab: GroupCheckPointTab.VIDEO_LICENSE_PLATE_NUMBER,
    defaultTab: GroupCheckPointTab.PHOTO_LICENSE_PLATE_NUMBER,
  });

  const { mutate: updateGroup } = useUpdateGroup({ t });

  const blurCheckPointLicensePlateValue =
    watch && watch(blurLicensePlateField as Path<TSchema>);

  const checkPointLicensePlateImageValue =
    watch && watch(licensePlateImageField as Path<TSchema>);

  /**
   * === SET INITIAL VALUES ===
   */
  useEffect(() => {
    if (setValue && data) {
      if (data?.groupCustomImage?.[blurLicensePlateField]) {
        setValue(
          blurLicensePlateField as Path<TSchema>,
          data?.groupCustomImage?.[blurLicensePlateField],
        );
      }

      if (data?.groupCustomImage?.[licensePlateImageField]) {
        setValue(
          licensePlateImageField as Path<TSchema>,
          data?.groupCustomImage?.[licensePlateImageField],
        );
      }

      if (data?.groupCustomImage?.[backgroundImageField]) {
        setValue(
          backgroundImageField as Path<TSchema>,
          data?.groupCustomImage?.[backgroundImageField],
        );
      }

      if (data?.groupCustomImage?.[watermarkField]) {
        setValue(
          watermarkField as Path<TSchema>,
          data?.groupCustomImage?.[watermarkField],
        );
      }

      if (data?.groupCustomImage?.[watermarkPositionField]) {
        setValue(
          watermarkPositionField as Path<TSchema>,
          data?.groupCustomImage?.[watermarkPositionField],
        );
      }
    }
  }, [
    setValue,
    data,
    blurLicensePlateField,
    licensePlateImageField,
    backgroundImageField,
    watermarkField,
    watermarkPositionField,
  ]);
  /**
   * === END OF SET INITIAL VALUES ===
   */

  const {
    mutate: uploadGroupCustomImage,
    isPending: isPendingUploadGroupCustomImage,
  } = useUploadGroupCustomImage({
    groupId: data?.id,
    filename: currentTab?.replace('Number', ''),
    handleSuccess: (response: {
      data: {
        filename: string;
      };
    }) => {
      if (data && mutate) {
        if (
          currentTab ===
          GroupCheckPointTab[`${checkPointType}_LICENSE_PLATE_NUMBER`]
        ) {
          updateGroup({
            ...data,
            groupCustomImage: {
              ...data?.groupCustomImage,
              [blurLicensePlateField]: false,
              [licensePlateImageField]: response.data.filename,
            },
          } as TSchema);
        } else if (
          currentTab ===
          GroupCheckPointTab[`${checkPointType}_BACKGROUND_IMAGE`]
        ) {
          updateGroup({
            ...data,
            groupCustomImage: {
              ...data?.groupCustomImage,
              [backgroundImageField]: response.data.filename,
            },
          } as TSchema);
        } else if (
          currentTab === GroupCheckPointTab[`${checkPointType}_WATERMARK`]
        ) {
          updateGroup({
            ...data,
            groupCustomImage: {
              ...data?.groupCustomImage,
              [watermarkField]: response.data.filename,
            },
          } as TSchema);
        }
      }
    },
  });

  return (
    <>
      <Card title={cardTitle}>
        {!submitDisabled && !data ? (
          <div className="mt-7 mb-6">
            <NotFound text={`${t('groupCheckPointNotFound')}...`} />
          </div>
        ) : (
          <>
            <TabList className="mb-5">
              <TabItem
                className={twMerge(
                  currentTab !==
                    GroupCheckPointTab[
                      `${checkPointType}_LICENSE_PLATE_NUMBER`
                    ] && 'border-black/50',
                )}
                text={t('common:licensePlateNumber_2')}
                leftIcon={<i className="ki-outline ki-barcode" />}
                active={
                  currentTab ===
                  GroupCheckPointTab[`${checkPointType}_LICENSE_PLATE_NUMBER`]
                }
                disabled={submitDisabled || isPendingUploadGroupCustomImage}
                onChange={() =>
                  navigateToTab(
                    GroupCheckPointTab[
                      `${checkPointType}_LICENSE_PLATE_NUMBER`
                    ],
                  )
                }
              />
              <TabItem
                className={twMerge(
                  currentTab !==
                    GroupCheckPointTab[`${checkPointType}_BACKGROUND_IMAGE`] &&
                    'border-black/50',
                )}
                text={`${t('common:backgroundImage')} (PNG)`}
                leftIcon={<i className="ki-outline ki-picture" />}
                active={
                  currentTab ===
                  GroupCheckPointTab[`${checkPointType}_BACKGROUND_IMAGE`]
                }
                disabled={submitDisabled || isPendingUploadGroupCustomImage}
                onChange={() =>
                  navigateToTab(
                    GroupCheckPointTab[`${checkPointType}_BACKGROUND_IMAGE`],
                  )
                }
              />
              <TabItem
                className={twMerge(
                  currentTab !==
                    GroupCheckPointTab[`${checkPointType}_WATERMARK`] &&
                    'border-black/50',
                )}
                text={`${t('common:watermark')} (PNG)`}
                leftIcon={<i className="ki-outline ki-fingerprint-scanning" />}
                active={
                  currentTab ===
                  GroupCheckPointTab[`${checkPointType}_WATERMARK`]
                }
                disabled={submitDisabled || isPendingUploadGroupCustomImage}
                onChange={() =>
                  navigateToTab(
                    GroupCheckPointTab[`${checkPointType}_WATERMARK`],
                  )
                }
              />
            </TabList>
            <div>
              {currentTab ===
                GroupCheckPointTab[
                  `${checkPointType}_LICENSE_PLATE_NUMBER`
                ] && (
                <GroupCheckPointLicensePlate<TSchema>
                  t={t}
                  blurCheckPointLicensePlateField={blurLicensePlateField}
                  checkPointLicensePlateImageField={licensePlateImageField}
                  blurCheckPointLicensePlateValue={
                    blurCheckPointLicensePlateValue
                  }
                  checkPointLicensePlateImageValue={
                    checkPointLicensePlateImageValue
                  }
                  data={data}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  submitDisabled={
                    submitDisabled || isPendingUploadGroupCustomImage
                  }
                  mutateUpdateGroup={updateGroup}
                  mutateUploadImage={uploadGroupCustomImage}
                />
              )}
              {currentTab ===
                GroupCheckPointTab[`${checkPointType}_BACKGROUND_IMAGE`] && (
                <GroupCheckPointBackgrounImage<TSchema>
                  t={t}
                  checkPointBackgroundImageField={backgroundImageField}
                  data={data}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  submitDisabled={
                    submitDisabled || isPendingUploadGroupCustomImage
                  }
                  mutateUploadImage={uploadGroupCustomImage}
                />
              )}
              {currentTab ===
                GroupCheckPointTab[`${checkPointType}_WATERMARK`] && (
                <GroupCheckPointWatermark<TSchema>
                  t={t}
                  checkPointWatermarkField={watermarkField}
                  checkPointWatermarkPositionField={watermarkPositionField}
                  data={data}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  submitDisabled={
                    submitDisabled || isPendingUploadGroupCustomImage
                  }
                  mutateUpdateGroup={updateGroup}
                  mutateUploadImage={uploadGroupCustomImage}
                />
              )}
            </div>
          </>
        )}
      </Card>
      {(submitDisabled || isPendingUploadGroupCustomImage) && <Loader />}
    </>
  );
};
